import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { TachesStoreService } from 'src/app/services/taches/taches.services';
import { panelRight, toggleSlideLeft, toggleSlideRight } from '../../../animations';
import { EtatTache, Tache, TacheEtatUtilisateurCount, TypeLien } from '../../../models/taches/gestion-taches.model';
import { GestionProjetService } from '../../../services/gestion-projet/gestion-projet.service';
import { SessionsService } from '../../../services/sessions/sessions-service';
import { Sorter } from '../../../utils/sort-util';

const minDate = new Date(-8640000000000000);
const minHour = "00:00";

@Component({
  selector: 'app-taches-panel',
  templateUrl: './taches-panel.component.html',
  styleUrls: ['./taches-panel.component.scss'],
  animations: [panelRight, toggleSlideLeft, toggleSlideRight],
  providers: [DatePipe]
})
export class TachesPanelComponent implements OnInit, OnDestroy {
  @HostBinding('class.panel-right') classPanelRight = true
  @HostBinding('@panelRight') state = 'panelRight';
  @ViewChild('divCursor') divCursor: ElementRef;
  @Output() fermer = new EventEmitter();
  @Input() set tache(value: Tache) {
    this.tacheEditee = value;
  }
  @Input() set isUpsertOnly(value: boolean) {
    this.isUpsertTacheOnly = value;
  }

  private isUpsertTacheOnly = false;
  private taches: Tache[] = [];
  private idUtilisateur: number;
  private tacheEtatCounts: TacheEtatUtilisateurCount[] = [];
  private subscriptions: Subscription[] = [];

  public eventTachesSubject: Subject<Tache[]> = new Subject<Tache[]>();
  public etatVisibles: EtatTache[] = [EtatTache.AFaire, EtatTache.EnCours];
  public tacheEditee: Tache;
  public tachesFiltering = [
    {
      label: () => this.tachesEnCoursCaption,
      tacheEtats: [EtatTache.AFaire, EtatTache.EnCours]
    },
    {
      label: () => this.tachesTermineesCaption,
      tacheEtats: [EtatTache.Termine]
    }
  ];
  public tachesVisibles = this.tachesFiltering[0];

  get isUpsertTacheVisibe(): boolean {
    return this.tacheEditee != null;
  }

  get tachesEnCoursCaption(): string {
    const tachesOuverteCount = this.getTachesOuverteCount();
    return `Mes tâches ` + (tachesOuverteCount > 0 ? `(${tachesOuverteCount})` : '')
  }

  get tachesTermineesCaption(): string {
    const tachesTermineeCount = this.getTachesTermineeCount();
    return `Tâches terminées ` + (tachesTermineeCount > 0 ? `(${tachesTermineeCount})` : '')
  }

  getTachesOuverteCount(): number {
    const aFaireCount = this.tacheEtatCounts.find(te => te.etat === EtatTache.AFaire)?.count ?? 0;
    const enCoursCount = this.tacheEtatCounts.find(te => te.etat === EtatTache.EnCours)?.count ?? 0;
    return aFaireCount + enCoursCount;
  }

  getTachesTermineeCount(): number {
    return this.tacheEtatCounts.find(te => te.etat === EtatTache.Termine)?.count ?? 0;
  }

  constructor(
    private gestionProjetService: GestionProjetService,
    private sessionsService: SessionsService,
    private tachesStore: TachesStoreService,
    ) {
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.tachesStore.toutesTaches$.subscribe(toutes => {
        const visibles = (toutes ?? []).filter(t =>
          this.etatVisibles.includes(t.etat)
        );
        this.eventTachesSubject.next(visibles);
      })
    );


    this.loadTaches();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(it => it.unsubscribe());
    this.subscriptions = [];
  }

  close() {
    this.fermer.emit();
  }

  fermerPanelModification() {
    this.tacheEditee = null;
    this.loadTaches();
    if (this.isUpsertTacheOnly) {
      this.isUpsertTacheOnly = false;
      this.close();
    }
  }

  requestModification(tache:Tache) {
    this.tacheEditee = tache;
  }

  onTacheModifieChange(tacheMaj: Tache) {
    this.tachesStore.updateTache(tacheMaj);
    this.fermerPanelModification();
  }

  onTacheSupprimee(idTache: number) {
    this.tachesStore.deleteTache(idTache);
    this.loadTaches();
  }

  onTacheEtatVisiblesChange(categorie) {
    this.tachesVisibles = categorie;
    this.etatVisibles = categorie.tacheEtats;
    this.loadTaches();
  }

  public loadTaches(tache?: Tache): void {
    this.gestionProjetService.getTacheEtatCounts().subscribe(
      tacheEtatCounts => this.tacheEtatCounts = tacheEtatCounts
    );
    this.gestionProjetService.getTachesListByEtats([
      EtatTache.AFaire,
      EtatTache.EnCours,
      EtatTache.Termine
    ]).subscribe(tachesUtilisateur => {
      this.sessionsService.getAllSessionIdsByUtilisateur().subscribe(idSessions => {
        const tachesFinales = (tachesUtilisateur.taches as Tache[]).map(tacheItem => {
          const liens = tacheItem.liens.map(lien => ({
            ...lien,
            isRedirectionDisabled: lien.typeLien === TypeLien.session && !idSessions.includes(lien.idOriginal)
          }));
          return { ...tacheItem, liens };
        });

        const tachesTriees = this.triTaches(tachesFinales);
        this.tachesStore.addOrUpdateTaches(tachesTriees);
      });
    });
  }



  private triTaches(taches: Tache[]): Tache[] {
    return new Sorter<Tache>()
      .orderBy(
        trierPar => trierPar.date(tache => tache.date ?? minDate),
        puisPar => puisPar.string(tache => tache.heureDebut ?? minHour),
        puisPar => puisPar.string(tache => tache.heureFin ?? minHour),
        puisPar => puisPar.string(tache => tache.titre ?? ''))
      .execute(taches)
  }
}

